import { useMemo, useRef } from 'react'
import RadioButtonChecked from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { QueryObserverResult } from 'react-query'

import AsyncEnhancedTable from 'src/components/shared/EnhancedTable/AsyncEnhancedTable'
import DatePickerField from 'src/components/shared/ui/DatePickerField'
import InputSearch from 'src/components/shared/ui/InputSearch'
import { Expedient } from 'src/interfaces'
import { GetExpedientsResponse } from 'src/services/ExpedientsService'
import useStyles, { useHeaderCellStyles } from './hooks/useStyles'

type Props = {
  expedientsQuery: QueryObserverResult<GetExpedientsResponse>
  onChangePaginationOptions: (value: any) => void
  onSelectExpedient: (expedient: Expedient) => void
  setClassificationName: (classificationName: string) => void
  setExpireDate: (expireDate: string) => void
  selectedExpedient: Expedient | null
  pageSize: number
  classificationName: string | undefined
  expireDate: string | undefined
}

function ExpedientsTable({
  expedientsQuery,
  onChangePaginationOptions,
  onSelectExpedient,
  setClassificationName,
  setExpireDate,
  selectedExpedient,
  pageSize,
  classificationName = '',
  expireDate = ''
}: Props): JSX.Element {
  const { t } = useTranslation()
  const pageCount = useRef<number | null>(null)
  const classes = useStyles()
  const columns = useMemo(
    () => [
      {
        Header: () => null,
        id: 'expander',
        width: 5,
        Cell: ({ row }) =>
          selectedExpedient?.idExpedient === row.original.idExpedient ? (
            <RadioButtonChecked
              color="primary"
              onClick={() => onSelectExpedient(row.original)}
              sx={{ fontSize: '20px' }}
            />
          ) : (
            <RadioButtonUncheckedIcon
              onClick={() => onSelectExpedient(row.original)}
              sx={{ fontSize: '20px' }}
            />
          )
      },
      {
        Header: 'Expediente',
        accessor: 'idOwnFile',
        width: 25
      },
      {
        Header: 'Cliente',
        accessor: 'customerName',
        width: 35
      },
      {
        Header: 'Descripción',
        accessor: 'description',
        width: 35
      }
    ],
    [onSelectExpedient, selectedExpedient?.idExpedient]
  )

  if (expedientsQuery.isSuccess) {
    pageCount.current = Math.ceil(expedientsQuery.data.total / pageSize)
  }

  return (
    <>
      <div className={classes.classificationName}>
        <InputSearch
          id="classification-name"
          label={t('form.field.classification_name')}
          placeholder={t('form.placeholders.classification_name')}
          clearable
          value={classificationName}
          maxLength={250}
          required
          onChange={setClassificationName}
          iconSearch={false}
        />
      </div>
      <Grid container spacing={2} className={classes.expireDate}>
        <Grid item xs={12} sm={6}>
          <DatePickerField
            id="expire-date"
            label={t('form.field.expire-date')}
            value={expireDate}
            onChange={setExpireDate}
          />
        </Grid>
      </Grid>

      <Typography color="secundary">
        ¿En qué expediente quieres clasificar la notificación?{' '}
        <span className={classes.spanRed}>*</span>
      </Typography>
      <div className={classes.tableContainer}>
        <AsyncEnhancedTable
          pageSize={pageSize}
          columns={columns}
          data={expedientsQuery.data?.results ?? []}
          onChangePaginationOptions={onChangePaginationOptions}
          pageCount={pageCount.current ?? 0}
          totalRowsCount={expedientsQuery.data?.total ?? 0}
          isFetching={expedientsQuery.isLoading}
          isError={expedientsQuery.isError}
          isSuccess={expedientsQuery.isSuccess}
          useHeaderCellStyles={useHeaderCellStyles}
          // simplePagination
          sortable={false}
        />
      </div>
    </>
  )
}

export default ExpedientsTable
